import { useState, useEffect } from 'react';
import { getAll, STORES } from '../db';
import { Search, Printer, Mail, ChevronDown } from 'lucide-react';

export default function Prescriptions() {
  const [patients, setPatients] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [supplierFilter, setSupplierFilter] = useState('');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    const [p, pr, s] = await Promise.all([getAll(STORES.patients), getAll(STORES.products), getAll(STORES.suppliers)]);
    setPatients(p.sort((a, b) => a.lastName?.localeCompare(b.lastName)));
    setProducts(pr.filter(p => p.status === 'Active' && p.type === 'Skincare').sort((a, b) => a.name?.localeCompare(b.name)));
    setSuppliers(s);
  };

  const toggleProduct = (id) => {
    setSelectedProducts(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const uniqueSuppliers = [...new Set(products.map(p => p.supplier).filter(Boolean))];

  const filteredProducts = products.filter(p =>
    (!supplierFilter || p.supplier === supplierFilter)
  );

  const selectedProductObjects = products.filter(p => selectedProducts.includes(p.id));

  const grouped = filteredProducts.reduce((acc, p) => {
    const cat = p.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const printPrescription = () => {
    const patient = selectedPatient;
    const prods = selectedProductObjects;
    if (!patient || prods.length === 0) {
      alert('Please select a patient and at least one product.');
      return;
    }

    const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Skincare Prescription — ${patient.firstName} ${patient.lastName}</title>
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300&family=Jost:wght@300;400;500&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Jost', sans-serif; font-weight: 300; color: #1a1a18; font-size: 13px; line-height: 1.6; padding: 40px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 1px solid #b8975a; margin-bottom: 24px; }
  .logo { font-size: 32px; font-family: 'Cormorant Garamond', serif; color: #b8975a; }
  .clinic-name { font-size: 13px; font-weight: 500; letter-spacing: 0.2em; text-transform: uppercase; }
  .clinic-sub { font-size: 10px; color: #7a7a72; letter-spacing: 0.1em; text-transform: uppercase; }
  .doc-title { font-family: 'Cormorant Garamond', serif; font-size: 20px; font-style: italic; color: #b8975a; text-align: right; }
  .patient-block { background: #faf8f4; padding: 16px 20px; margin-bottom: 24px; border: 1px solid #ddd8cc; }
  .patient-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
  .field { display: flex; flex-direction: column; }
  .field-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.12em; color: #7a7a72; }
  .field-val { font-size: 13px; }
  .section-head { display: flex; align-items: center; gap: 12px; margin: 20px 0 10px; }
  .section-label { font-family: 'Cormorant Garamond', serif; font-size: 18px; font-style: italic; color: #b8975a; white-space: nowrap; }
  .section-line { flex: 1; height: 1px; background: #b8975a; opacity: 0.3; }
  .sub-head { font-size: 9px; text-transform: uppercase; letter-spacing: 0.15em; color: #3d3d38; margin: 12px 0 4px; }
  .product-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; padding: 8px 0; border-bottom: 1px solid #f0ece4; }
  .product-name { font-size: 13px; font-weight: 400; }
  .product-desc { font-size: 11px; color: #7a7a72; margin-top: 2px; }
  .product-usage { font-size: 11px; color: #3d3d38; margin-top: 2px; font-style: italic; }
  .product-rrp { font-size: 13px; color: #3d3d38; font-weight: 400; }
  .notes-block { margin-top: 24px; padding: 16px; border: 1px solid #ddd8cc; }
  .notes-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.12em; color: #7a7a72; margin-bottom: 6px; }
  .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #b8975a; display: flex; justify-content: space-between; font-size: 9px; color: #7a7a72; text-transform: uppercase; letter-spacing: 0.08em; }
  @media print { body { padding: 20px; } }
</style>
</head>
<body>
<div class="header">
  <div>
    <div class="logo">DA</div>
    <div class="clinic-name">Dorset Aesthetics</div>
    <div class="clinic-sub">Skin & Wellness · Blandford Forum</div>
  </div>
  <div>
    <div class="doc-title">Skincare Prescription</div>
    <div style="font-size:10px;color:#7a7a72;text-align:right;margin-top:3px;letter-spacing:0.1em;text-transform:uppercase;">Confidential</div>
  </div>
</div>

<div class="patient-block">
  <div class="patient-grid">
    <div class="field"><div class="field-label">Patient Name</div><div class="field-val">${patient.firstName} ${patient.lastName}</div></div>
    <div class="field"><div class="field-label">Date of Birth</div><div class="field-val">${patient.dob ? new Date(patient.dob).toLocaleDateString('en-GB') : '—'}</div></div>
    <div class="field" style="grid-column:1/-1"><div class="field-label">Address</div><div class="field-val">${[patient.address1, patient.address2, patient.town, patient.county, patient.postcode].filter(Boolean).join(', ') || '—'}</div></div>
    <div class="field"><div class="field-label">Telephone</div><div class="field-val">${patient.phone || '—'}</div></div>
    <div class="field"><div class="field-label">Email</div><div class="field-val">${patient.email || '—'}</div></div>
    <div class="field"><div class="field-label">Prescription Date</div><div class="field-val">${new Date().toLocaleDateString('en-GB')}</div></div>
    <div class="field"><div class="field-label">Prescribing Clinician</div><div class="field-val">Gilly Graver, Independent Nurse Prescriber</div></div>
  </div>
</div>

${['Cleanse', 'Treat', 'Protect'].map(section => {
  const sectionProds = prods.filter(p => p.category?.startsWith(section));
  if (sectionProds.length === 0) return '';
  const subGroups = {};
  sectionProds.forEach(p => {
    const sub = p.category?.replace(section, '').replace('—', '').trim() || section;
    if (!subGroups[sub]) subGroups[sub] = [];
    subGroups[sub].push(p);
  });
  return `
<div class="section-head"><div class="section-line"></div><div class="section-label">${section}</div><div class="section-line"></div></div>
${Object.entries(subGroups).map(([sub, items]) => `
  ${sub !== section ? `<div class="sub-head">${sub}</div>` : ''}
  ${items.map(p => `
  <div class="product-row">
    <div>
      <div class="product-name">${p.name}${p.size ? ` <span style="font-size:11px;color:#7a7a72;">${p.size}</span>` : ''}</div>
      ${p.description ? `<div class="product-desc">${p.description}</div>` : ''}
      ${p.howToUse || p.whenToUse ? `<div class="product-usage">${[p.whenToUse, p.howToUse].filter(Boolean).join(' · ')}</div>` : ''}
    </div>
    <div class="product-rrp">${p.rrp ? `£${parseFloat(p.rrp).toFixed(2)}` : ''}</div>
  </div>`).join('')}
`).join('')}`;
}).join('')}

${notes ? `<div class="notes-block"><div class="notes-label">Clinician Notes</div><div>${notes}</div></div>` : ''}

<div class="footer">
  <span>Dorset Aesthetics Ltd · www.dorsetaesthetics.co.uk</span>
  <span>All RRPs include VAT · Prescribed by a qualified Independent Nurse Prescriber</span>
</div>

<script>window.print();<\/script>
</body>
</html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div>
          <h2 className="section-title">Prescriptions</h2>
          <p className="section-sub">Generate a skincare prescription for a patient</p>
        </div>
      </div>

      <div className="prescription-layout">
        <div className="prescription-left">
          <div className="prescription-panel">
            <div className="panel-label">1. Select Patient</div>
            <select className="patient-select" value={selectedPatient?.id || ''} onChange={e => setSelectedPatient(patients.find(p => p.id === parseInt(e.target.value)) || null)}>
              <option value="">Choose patient...</option>
              {patients.map(p => <option key={p.id} value={p.id}>{p.lastName}, {p.firstName}</option>)}
            </select>
            {selectedPatient && (
              <div className="selected-patient-preview">
                <div className="patient-name">{selectedPatient.firstName} {selectedPatient.lastName}</div>
                <div className="patient-meta">{[selectedPatient.address1, selectedPatient.town, selectedPatient.postcode].filter(Boolean).join(', ')}</div>
                {(selectedPatient.skinConditions || []).map(c => <span key={c} className="mini-tag">{c}</span>)}
              </div>
            )}
          </div>

          <div className="prescription-panel" style={{ marginTop: '1rem' }}>
            <div className="panel-label">2. Filter by Supplier (optional)</div>
            <select className="patient-select" value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}>
              <option value="">All suppliers</option>
              {uniqueSuppliers.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {supplierFilter && <div className="supplier-note">Prescription will only show {supplierFilter} products — safe to email directly to them.</div>}
          </div>

          <div className="prescription-panel" style={{ marginTop: '1rem' }}>
            <div className="panel-label">3. Clinician Notes</div>
            <textarea className="notes-box" value={notes} onChange={e => setNotes(e.target.value)} rows={4} placeholder="Routine instructions, review date..." />
          </div>

          <div className="prescription-panel" style={{ marginTop: '1rem' }}>
            <div className="panel-label">Selected: {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''}</div>
            {selectedProductObjects.map(p => (
              <div key={p.id} className="selected-product-item">
                <span>{p.name}</span>
                <button onClick={() => toggleProduct(p.id)} className="icon-btn danger"><ChevronDown size={12} /></button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
              <button className="btn-gold" onClick={printPrescription} style={{ flex: 1 }}>
                <Printer size={14} /> Print / PDF
              </button>
            </div>
          </div>
        </div>

        <div className="prescription-right">
          <div className="panel-label">Select Products</div>
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="product-category-group">
              <div className="category-group-header">{cat}</div>
              {items.map(p => (
                <div key={p.id} className={`prescription-product-row ${selectedProducts.includes(p.id) ? 'selected' : ''}`}
                  onClick={() => toggleProduct(p.id)}>
                  <input type="checkbox" checked={selectedProducts.includes(p.id)} onChange={() => toggleProduct(p.id)} onClick={e => e.stopPropagation()} />
                  <div>
                    <div className="product-name">{p.name} {p.size && <span className="product-size">{p.size}</span>}</div>
                    {p.description && <div className="product-size">{p.description.slice(0, 80)}{p.description.length > 80 ? '...' : ''}</div>}
                  </div>
                  <div className="product-rrp">{p.rrp ? `£${parseFloat(p.rrp).toFixed(2)}` : ''}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
